import { addDays, format, isSameDay } from "date-fns";
import { enUS, es, fr, he, ru } from "date-fns/locale";
import type { JourneyTaskOccurrence } from "../../../domain/track/journeyTaskOccurrenceAdapter";
import { useToday } from "../../../hooks/track/useToday";
import { useClientTranslations } from "../../../lib/i18n/client";
import {
	resolveTaskDomain,
	TASK_DOMAIN_COLORS,
} from "../../../lib/journey/taskDomainColors";
import { getEventTypeColor } from "../../../lib/track/eventTypeColor";
import type { Event } from "../../../types/event";
import styles from "./TwoWeekCalendar.module.css";

const LOCALE_MAP = { en: enUS, es, fr, he, ru } as const;

/**
 * M011 S04 T03 — calendar cells can carry manual Events and journey-derived
 * task occurrences. The two shapes are discriminated by `source`: Event has
 * `'patient' | 'clinic' | undefined`, JourneyTaskOccurrence has the literal
 * `'journey'`. We don't mutate Event — we widen the renderer to a union and
 * thread it through onDayTap so the page can dispatch each tap correctly.
 */
type CalendarItem = Event | JourneyTaskOccurrence;

/** Represents one colored dot in a calendar cell. */
interface DotSpec {
	/** Unique key for React rendering — color hex or CSS class key. */
	key: string;
	/** CSS class suffix for event dots (e.g. "meds", "cycle"). Absent for journey dots. */
	colorClass?: string;
	/** Inline hex color for journey dots resolved from taskDomain. Absent for event dots. */
	inlineColor?: string;
}

function isJourneyOccurrence(
	item: CalendarItem,
): item is JourneyTaskOccurrence {
	return item.source === "journey";
}

function itemDayKey(item: CalendarItem): string {
	return isJourneyOccurrence(item)
		? item.startIso.slice(0, 10)
		: item.dateTime.slice(0, 10);
}

interface TwoWeekCalendarProps {
	windowStart: Date;
	events: Event[];
	/**
	 * M011 S04 T03 — journey task occurrences derived from useJourneyProgress.
	 * Rendered alongside manual events with a distinct accent color. Each
	 * occurrence's start day determines the calendar cell it appears in.
	 */
	journeyOccurrences?: JourneyTaskOccurrence[];
	onDayTap: (dayIso: string, itemsForDay: CalendarItem[]) => void;
	/**
	 * Phase 38 D-12 — ISO yyyy-MM-dd. The day cell whose date matches this
	 * value pulses for ~1.5s. Triggered whenever `focusDayIso` is set to a
	 * matching value — including post-mount updates (e.g., when a deep-link
	 * notification is tapped while TrackPage is already mounted, the cell
	 * re-pulses on each consumption).
	 */
	focusDayIso?: string | null;
	/**
	 * ISO yyyy-MM-dd of the currently selected day. The selected cell carries
	 * the highlight border (which follows the active day in the Coming-Up
	 * feed). Defaults to today when unset so a stand-alone calendar still
	 * highlights the current day.
	 */
	selectedDayIso?: string | null;
}

export function TwoWeekCalendar({
	windowStart,
	events,
	journeyOccurrences,
	onDayTap,
	focusDayIso,
	selectedDayIso,
}: TwoWeekCalendarProps) {
	const { t, locale } = useClientTranslations("track");
	const dateFnsLocale = LOCALE_MAP[locale as keyof typeof LOCALE_MAP] ?? enUS;
	const { today, todayIso } = useToday();
	// The highlight border follows the selected day (defaults to today), so it
	// moves to whichever day the user taps instead of staying pinned on today.
	const selectedIso = selectedDayIso ?? todayIso;

	const days = Array.from({ length: 14 }, (_, i) => addDays(windowStart, i));

	// Pitfall 2: day-grouping via slice (NOT parseISO) so wall-clock TZ semantic preserved.
	// T03: merge events and journey occurrences into the same per-day buckets.
	const byDay = new Map<string, CalendarItem[]>();
	for (const e of events) {
		const key = e.dateTime.slice(0, 10);
		const arr = byDay.get(key) ?? [];
		arr.push(e);
		byDay.set(key, arr);
	}
	if (journeyOccurrences) {
		for (const occ of journeyOccurrences) {
			const key = itemDayKey(occ);
			const arr = byDay.get(key) ?? [];
			arr.push(occ);
			byDay.set(key, arr);
		}
	}

	const dayHeaders = Array.from({ length: 7 }, (_, i) =>
		format(addDays(windowStart, i), "EEEEEE", {
			locale: dateFnsLocale,
		}).toUpperCase(),
	);

	return (
		<div className={styles.calendar}>
			<div className={styles.dayHeaderRow}>
				{dayHeaders.map((header, idx) => (
					<div key={`${header}-${idx}`} className={styles.dayHeader}>
						{header}
					</div>
				))}
			</div>
			<div className={styles.cells}>
				{days.map((d) => {
					const iso = format(d, "yyyy-MM-dd");
					const dayItems = byDay.get(iso) ?? [];
					const hasEvents = dayItems.length > 0;
					const isToday = isSameDay(d, today);
					const isSelected = iso === selectedIso;
					const isFocusDay = focusDayIso === iso;
					// M011 S07 T11 — build deduplicated dot specs. Event dots use CSS class
					// coloring; journey dots resolve per-domain color via TASK_DOMAIN_COLORS.
					const seenKeys = new Set<string>();
					const dots: DotSpec[] = [];
					for (const item of dayItems) {
						if (isJourneyOccurrence(item)) {
							const domain = resolveTaskDomain(item.taskDomain);
							const color = TASK_DOMAIN_COLORS[domain];
							if (!seenKeys.has(color)) {
								seenKeys.add(color);
								dots.push({ key: color, inlineColor: color });
							}
						} else {
							const colorClass = getEventTypeColor(item.type);
							if (!seenKeys.has(colorClass)) {
								seenKeys.add(colorClass);
								dots.push({ key: colorClass, colorClass });
							}
						}
						if (dots.length >= 4) break;
					}
					const cellClass = [
						styles.cell,
						// Today always keeps its emphasis (bold number + bigger dots);
						// the selected day gets the same emphasis plus the highlight
						// border, so when another day is selected today still stands out.
						isToday ? styles.cellToday : "",
						isSelected ? styles.cellSelected : "",
						!hasEvents ? styles.cellEmpty : "",
						isFocusDay ? styles.cellPulse : "",
					]
						.filter(Boolean)
						.join(" ");
					return (
						<button
							key={iso}
							type="button"
							className={cellClass}
							aria-current={isToday ? "date" : undefined}
							aria-pressed={isSelected}
							aria-label={t("calendar.dayCellLabel", {
								date: format(d, "PPPP", { locale: dateFnsLocale }),
								count: dayItems.length,
							})}
							// 86ey0mc8v — every day is tappable so the Coming-Up feed
							// follows the selection; tapping an empty day shows that
							// day's empty state instead of leaving the previous day's
							// items stale. (Empty days stay visually muted via cellEmpty.)
							onClick={() => onDayTap(iso, dayItems)}
						>
							<span className={styles.dayNumber}>{format(d, "d")}</span>
							{dots.length > 0 && (
								<div className={styles.dotsRow}>
									{dots.map((dot) => (
										<span
											key={dot.key}
											className={
												dot.colorClass
													? `${styles.dot} ${styles[`dot--${dot.colorClass}`]}`
													: styles.dot
											}
											style={
												dot.inlineColor
													? { backgroundColor: dot.inlineColor }
													: undefined
											}
											aria-hidden="true"
										/>
									))}
								</div>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
}
